import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, Chip, Divider
} from '@mui/material';
import { crearTramiteAmparoPnc, getChecklistFormularios, getTramitesAmparoPnc } from '../../services/amparoPncService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import BotonGenerarAmparoPnc from './BotonGenerarAmparoPnc';
import useEscritoPendiente from '../Documentos/useEscritoPendiente';
import { generarEscritoAmparoPncTramite } from '../../services/amparoPncEscritoService';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

/**
 * Formulario para iniciar un trámite de Amparo PNC (amparo por mora de la PNC). Como PUAM/PNC,
 * es plano: sin subtipo. El checklist es siempre el mismo (pronto despacho + amparo + copia CMO).
 * Solo inicio + ficha por ahora; la parte judicial (instancia/estados) se agrega más adelante.
 */
// Amparo Ley 16.986 (con CUD, V256): por ahora una variante de este módulo. Mismo formulario,
// mismos estados y misma ficha; cambia el tipo de trámite con el que nace y su carátula.
export const VARIANTE_LEY_16986 = 'LEY_16986';

export default function FormularioIniciarTramiteAmparoPnc({ onSuccess, onCancel, cuilInicial, idTurnoOrigen, variante }) {
    const navigate = useNavigate();
    const esLey16986 = variante === VARIANTE_LEY_16986;
    const nombreTipo = esLey16986 ? 'Amparo Ley 16.986' : 'Amparo PNC';
    const baseRuta = esLey16986 ? '/amparo-ley-16986' : '/amparo-pnc';
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    // Mismo diferido que el pacto, para el escrito de amparo: emitido acá, adjuntado al nacer el
    // trámite (antes se descargaba y no quedaba en los Archivos de la ficha).
    const { registrarEscrito, adjuntarATramite: adjuntarEscrito } = useEscritoPendiente(generarEscritoAmparoPncTramite);
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        organismo: '',
        cmoIncorporado: false,
        tieneCopiaCmo: false,
        tieneCud: false,
        fechaInicioTramitePnc: '',
        nroExpedientePnc: '',
        // Renglones de la Carátula (ver components/Caratula). Se pueden cargar acá o más tarde
        // desde el modal de carátula. OBSERVACIONES no está: iría pegado al campo
        // "Observaciones" de abajo, que es otra cosa (tramite.comentarios, la nota firmada).
        estadoExpedientePnc: '',
        envioProntoDespacho: '',
        envioAmparo: '',
        comentarios: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Amparo PNC duplicado para el mismo cliente (patrón RTI/ART/Jubilación/PUAM/Reconocimiento/PNC) ---
    const [tieneDuplicado, setTieneDuplicado] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Checklist (siempre los mismos 3 formularios: se trae una sola vez) ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(true);

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Preview del checklist: Amparo PNC no tiene condicionales, se trae una sola vez.
    useEffect(() => {
        const cargarChecklist = async () => {
            setCargandoChecklist(true);
            try {
                const data = await getChecklistFormularios();
                setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                setChecklist([]);
            } finally {
                setCargandoChecklist(false);
            }
        };
        cargarChecklist();
    }, []);

    // Trámite de Amparo PNC ya existente para el cliente seleccionado (patrón RTI/ART/Jubilación/PUAM/Reconocimiento/PNC).
    // No tiene estados de cierre, así que "activo" = existe cualquier trámite con ese CUIL.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicado(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesAmparoPnc(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicado(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Amparo PNC duplicados:', err);
                if (activo) {
                    setTieneDuplicado(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (tieneDuplicado && !confirmarDuplicado) {
            setError(`Debe confirmar que desea crear otro trámite de ${nombreTipo} para un cliente que ya tiene uno.`);
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                organismo: form.organismo || null,
                cmoIncorporado: form.cmoIncorporado,
                tieneCopiaCmo: form.tieneCopiaCmo,
                tieneCud: form.tieneCud,
                fechaInicioTramitePnc: form.fechaInicioTramitePnc || null,
                nroExpedientePnc: form.nroExpedientePnc,
                estadoExpedientePnc: form.estadoExpedientePnc,
                envioProntoDespacho: form.envioProntoDespacho || null,
                envioAmparo: form.envioAmparo || null,
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
                variante: esLey16986 ? VARIANTE_LEY_16986 : null,
            };
            const creado = await crearTramiteAmparoPnc(payload);
            // El pacto y el escrito se emitieron antes de que el trámite existiera: recién ahora
            // se pueden adjuntar.
            await adjuntarATramite(creado.idTramite);
            await adjuntarEscrito(creado.idTramite);
            setSuccess(`Trámite de ${nombreTipo} creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`${baseRuta}/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="shield-exclamation"
                        titulo={`Iniciar Trámite de ${nombreTipo}`}
                        descripcion="Complete los datos para registrar un amparo por mora de la PNC"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-amparo-pnc"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {/* Datos del trámite */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}

                            <TextField select label="Organismo" name="organismo" value={form.organismo} onChange={handleChange} fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia, ANSES o ANDIS.">
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                                <MenuItem value="ANDIS">ANDIS</MenuItem>
                            </TextField>

                            <TextField
                                label="Fecha de inicio del trámite PNC"
                                name="fechaInicioTramitePnc"
                                type="date"
                                value={form.fechaInicioTramitePnc}
                                onChange={handleChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Fecha en que se inició el trámite PNC que está demorado (referencia: el amparo se presenta a los ~6 meses)."
                            />

                            <TextField
                                label="N° de expediente PNC"
                                name="nroExpedientePnc"
                                value={form.nroExpedientePnc}
                                onChange={handleChange}
                                fullWidth
                                helperText="Opcional: referencia al expediente PNC demorado."
                            />

                            <FormControlLabel
                                control={<Checkbox name="cmoIncorporado" checked={form.cmoIncorporado} onChange={handleCheck} />}
                                label="CMO incorporado con anterioridad"
                            />
                            <FormControlLabel
                                control={<Checkbox name="tieneCopiaCmo" checked={form.tieneCopiaCmo} onChange={handleCheck} />}
                                label="Se cuenta con copia del CMO"
                            />
                            <FormControlLabel
                                control={<Checkbox name="tieneCud" checked={form.tieneCud} onChange={handleCheck} />}
                                label="El cliente tiene CUD (Certificado Único de Discapacidad)"
                            />

                            <TextField label="Estado actual del expediente PNC" name="estadoExpedientePnc"
                                value={form.estadoExpedientePnc} onChange={handleChange} fullWidth
                                helperText="Se imprime en la carátula. Es el estado que muestra ANSES del expediente demorado." />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Día que se envió el pronto despacho" name="envioProntoDespacho"
                                    type="date" value={form.envioProntoDespacho} onChange={handleChange} fullWidth
                                    InputLabelProps={{ shrink: true }} />
                                <TextField label="Día que se envió el amparo" name="envioAmparo"
                                    type="date" value={form.envioAmparo} onChange={handleChange} fullWidth
                                    InputLabelProps={{ shrink: true }} />
                            </Stack>

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Observaciones libres sobre el trámite." />

                            {/* El escrito es el del amparo PNC por mora: no va en el Amparo Ley 16.986. */}
                            {!esLey16986 && <BotonGenerarAmparoPnc
                                datos={{
                                    cuil: form.idCliente,
                                    organismo: form.organismo,
                                    tieneCopiaCmo: form.tieneCopiaCmo,
                                    tieneCud: form.tieneCud,
                                    nroExpedientePnc: form.nroExpedientePnc,
                                    fechaInicioTramitePnc: form.fechaInicioTramitePnc || null,
                                    envioProntoDespacho: form.envioProntoDespacho || null,
                                }}
                                onGenerado={registrarEscrito}
                            />}

                            {/* Checklist (siempre los mismos 3 formularios) */}
                            <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                    <i className="bi bi-check2-square me-2"></i>
                                    Formularios requeridos {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {checklist.length === 0 && !cargandoChecklist ? (
                                    <Typography variant="body2" color="text.secondary">Sin formularios configurados.</Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {checklist.map(f => <Chip key={f.codigo} label={f.nombre} size="small" color="primary" variant="outlined" />)}
                                    </Stack>
                                )}
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicado && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Amparo PNC' : `${cantidadDuplicados} trámites de Amparo PNC`} (incluye los de Amparo Ley 16.986). ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicado && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label={`Confirmo que deseo continuar con un nuevo trámite de ${nombreTipo} igualmente.`}
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Amparo PNC activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
